import Title from 'components/title';
import React, { useEffect } from 'react';
import { formatCnpj, formatMoney, getLoanApplicationParams } from 'utils/utils';
import PaymentSummary from 'components/payment-summary';
import { useNavigation } from 'hooks';
import Loading from 'components/loading';
import AlertMessage from 'components/alert-message';
import smartphoneIcon from 'assets/svg/smatphone-icon.svg';
import './loan-information.scss';
import ButtonGroup from 'components/button-group';
import { Button } from 'components/commons';
import { useAcceptedProposal } from 'providers/accepted-proposal.provider';
import { useAntiFraudLazyQuery, useSetAcceptedProposalMutation, useGetLoanQuery } from 'generated/graphql';
import { APP_PROFILE } from 'env';

const LoanInformation: React.FC = () => {
    const loanApplicationId = getLoanApplicationParams();
    const [setAcceptedProposalMutation] = useSetAcceptedProposalMutation();

    const { data } = useGetLoanQuery({
        variables: {
            id: loanApplicationId
        },
        fetchPolicy: "cache-first"
    });

    const {
        acceptedProposal,
    } = useAcceptedProposal();
    const {
        goToLoanConfiguration,
        goToAntiFraudCheckout,
        goToShareAntiFraudUrl,
        nextScreen
    } = useNavigation();

    const [antiFraudQuery] = useAntiFraudLazyQuery({
        fetchPolicy: "network-only"
    });


    useEffect(() => {
        if (!acceptedProposal?.simulation || !acceptedProposal?.form) {
            goToLoanConfiguration();
        }
    }, [acceptedProposal]);

    const onSubmit = () => {
        setAcceptedProposalMutation({
            variables: {
                loanApplicationId: loanApplicationId,
                proposal: acceptedProposal.proposal,
                insuranceAccepted: true
            }
        });

        redirect();
    }

    const redirect = async () => {
        try {
            const response = await antiFraudQuery({
                variables: {
                    loanApplicationId: loanApplicationId
                }
            })

            if (response.data.antiFraud) {
                if (APP_PROFILE !== 'partners') {
                    goToAntiFraudCheckout();
                } else {
                    goToShareAntiFraudUrl();
                }
            } else {
                nextScreen('/:loanApplicationId/configuracao');
            }
        } catch {
            nextScreen('/:loanApplicationId/configuracao');
        }
    }

    return (
        !acceptedProposal?.simulation || !acceptedProposal?.form ? <Loading /> :
            <div className="mt-0 hidden-mobile loan-information loan-page">
                <span className="loan-information__document">
                    CNPJ {formatCnpj('88112444000160')}
                </span>
                <Title className="loan-information__title">pagamento automático via recebíveis </Title>
                <div className="loan-information__details">
                    <p className="body-1">
                        Simplificamos seu empréstimo! Seus pagamentos serão
                        automaticamente descontados dos valores que você recebe.
                    </p>
                    <div className='loan-information__details--timeline'>
                        <div data-icon="../assets/svg/calendar.svg">
                            <b>Consulta dos seus recebíveis</b> <br />
                            Depois da assinatura do contrato, consultamos novamente a sua agenda de
                            recebíveis para entender quais pagamentos podem cobrir sua parcela.
                        </div>
                        <div data-icon="../assets/svg/wallet.svg">
                            <b>Escolha dos recebíveis que garantem o contrato</b> <br />
                            Identificamos automaticamente quais linhas têm valor suficiente para
                            garantir o pagamento da sua parcela mensal.                        </div>
                        <div data-icon="../assets/svg/money1.svg">
                            <b>Envio para a conta-garantia</b> <br />
                            A linha escolhida passa a enviar os valores primeiro para uma conta-garantia,
                            que apenas organiza o fluxo antes do repasse diário.
                        </div>
                        <div data-icon="../assets/svg/money2.svg">
                            <b>Sem bloqueio do seu dinheiro</b> <br />
                            Os valores não ficam travados: seguem normalmente para sua conta de uso livre,
                            sem bloqueios ou restrições.
                        </div>
                        <div data-icon="../assets/svg/confirm.svg">
                            <b>Pagamento automático da parcela</b> <br />
                            Dois dias antes do vencimento, usamos apenas
                            o valor necessário para pagar sua parcela e repassamos o restante para você.
                        </div>
                    </div>
                    <AlertMessage
                        type="warning"
                        title="Informação Importante"
                        message="Se os seus recebíveis não forem suficientes para cobrir a parcela,
                         você receberá uma notificação para complementar o valor via PIX."
                    >
                        <p className='loan-information__details--alert'><img className="icon" src={smartphoneIcon} /> Processo rápido e seguro</p>
                    </AlertMessage>
                </div>
                <div className="loan-information__card">
                    <PaymentSummary
                        proposal={acceptedProposal.form}
                        simulation={acceptedProposal.simulation}
                        insurance={acceptedProposal.insuranceAccepted}
                        productBizId={data?.loanApplication.product.bizId}
                    >
                        <ButtonGroup className="buttons-group-mobile">
                            <Button
                                label="continuar"
                                className="grid-button-forward"
                                onClick={() => onSubmit()}
                            />
                        </ButtonGroup>
                    </PaymentSummary>
                </div>

                <div className="loan-information__advantages">
                    <ul className="loan-information__advantages-list">
                        <h1>Vantagens para Você</h1>
                        <li>Transparência total, sem taxas escondidas ou surpresas.</li>
                        <li>Processo 100% automático e seguro.</li>
                        <li>Contratação sem burocracia.</li>
                    </ul>

                </div>
                <div className="loan-information__footer only-desktop">
                    <ButtonGroup className="button-group">
                        <Button
                            label="voltar"
                            className="grid-button-backward"
                            variant="secondary"
                            onClick={() => goToLoanConfiguration()}
                        />
                        <p>
                            {acceptedProposal.proposal.tenure} parcelas de  <strong>{formatMoney(acceptedProposal.simulation.pmt)}</strong>
                            <Button
                                label="continuar"
                                className={` grid-button-forward ml-4`}
                                loading={false}
                                onClick={() => onSubmit()}
                            />
                        </p>
                    </ButtonGroup>
                </div>
            </div>
    );
}

export default LoanInformation;